import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Container,
  Paper,
  Select,
  MenuItem,
  CircularProgress,
  Collapse,
  IconButton,
  Tooltip,
  Button,
  LinearProgress,
  Chip,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import BarChartIcon from "@mui/icons-material/BarChart";
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import SchoolIcon from '@mui/icons-material/School';
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AnswerRow from "../ActiveSessionsPage/AnswerRow";
import Header from "../../../components/Header/Header";
import useAxios from "../../../hooks/useAxios";
import "./TeacherSpecificPage.css";
import StyledSelect from "../../../components/common/StyledComponents/StyledSelect";
import ContentHeader from "../../../components/common/ContentHeader/ContentHeader";
import ReportModal from "../../../components/Reporting/Modals/ReportModal/ReportModal";
import ArticleIcon from "@mui/icons-material/Article";

const navLinks = [];

function TeacherSpecificPage() {
  const axiosInstance = useAxios();
  const [teachers, setTeachers] = useState([]);
  const [teacherSearch, setTeacherSearch] = useState("");
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedSessionId, setExpandedSessionId] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [currentReport, setCurrentReport] = useState(null);

  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        const response = await axiosInstance.get("/users/teachers");
        if (response.data.success) {
          setTeachers(response.data.teachers);
        }
      } catch (error) {
        toast.error("Error fetching teachers.");
      }
    };
    fetchTeachers();
  }, []);

  const handleTeacherSelect = async (teacherId) => {
    setSelectedTeacher(teacherId);
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/school-report/teacher/${teacherId}`);
      setReport(response.data);
      toast.success("Teacher report loaded successfully!");
    } catch (error) {
      toast.error("Error fetching teacher report.");
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (sessionId) => {
    setExpandedSessionId((prev) => (prev === sessionId ? null : sessionId));
  };

  const filteredTeachers = teachers.filter((teacher) =>
    teacher.full_name.toLowerCase().includes(teacherSearch.toLowerCase())
  );

  const handleViewReportClick = async (sessionId) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/sessions/${sessionId}/report`);
      if (response.data.success) {
        setCurrentReport(response.data.report);
        setReportModalOpen(true);
      } else {
        toast.error(response.data.message || "Failed to fetch report.");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-theme={darkMode ? "dark" : "light"}>
      <Header navLinks={navLinks} isScrollEffect={false} />
      <Container className="teacher-report-container page-content">
        <Box className="page-header">
          <ContentHeader title="Select Teacher" />
          <Box>
            {/* <TextField
              placeholder="Search Teacher"
              value={teacherSearch}
              onChange={(e) => setTeacherSearch(e.target.value)}
              size="small"
              variant="outlined"
              className="teacher-search"
            /> */}
            <StyledSelect
              value={selectedTeacher || ""}
              onChange={(e) => handleTeacherSelect(e.target.value)}
              displayEmpty
              className="teacher-select"
            >
              <MenuItem value="" disabled>
                Select a Teacher
              </MenuItem>
              {filteredTeachers.map((teacher) => (
                <MenuItem key={teacher.user.id} value={teacher.user.id}>
                  {teacher.full_name}
                </MenuItem>
              ))}
            </StyledSelect>
          </Box>
        </Box>

        {loading && (
          <Box className="loading-container">
            <CircularProgress />
            <Typography>Loading report...</Typography>
          </Box>
        )}

        {report && (
          <Box>
            {/* Summary Cards */}
            <Box className="summary-cards">
              <Paper className="summary-card" onClick={() => {}}>
                <Box className="card-content">
                  <Box className="icon-container">
                    <QuestionAnswerIcon fontSize="large" />
                  </Box>
                  <Box>
                    <Typography variant="h5">{report.total_quizzes}</Typography>
                    <Typography variant="subtitle1">Total Assessments</Typography>
                  </Box>
                </Box>
              </Paper>
              <Paper className="summary-card" onClick={() => {}}>
                <Box className="card-content">
                  <Box className="icon-container">
                    <SchoolIcon fontSize="large" />
                  </Box>
                  <Box>
                    <Typography variant="h5">{report.total_sessions}</Typography>
                    <Typography variant="subtitle1">Total Sessions</Typography>
                  </Box>
                </Box>
              </Paper>
              <Paper className="summary-card">
                <Box className="card-content">
                  <Box className="icon-container">
                    <BarChartIcon fontSize="large" />
                  </Box>
                  <Box>
                    <Typography variant="h5">
                      {Math.round(
                        report.quizzes.reduce((sum, quiz) => sum + quiz.sessions.length, 0) /
                          report.total_sessions || 0
                      )}
                    </Typography>
                    <Typography variant="subtitle1">Avg. Sessions/Assessment</Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>
            {/* Teacher Details */}
            <Paper elevation={2} className="teacher-details">
              <Box className="details-header">
                <Box className="avatar-container">
                  <Typography className="avatar-initials">
                    {report.teacher.name.split(' ').map((n) => n[0]).join('')} {/* Initials */}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h6" className="teacher-name">
                    {report.teacher.name}
                  </Typography>
                  <Typography variant="body2" className="teacher-role">
                    {report.teacher.role || "Teacher"}
                  </Typography>
                </Box>
              </Box>
                <Box className="contact-details">
                  <Typography>
                  <Tooltip title="Email" arrow>
                      ✉️{" "}
                      <a href={`mailto:${report.teacher.email}`} className="contact-link">
                      {report.teacher.email}
                      </a>
                  </Tooltip>
                  </Typography>
                  <Typography>
                  <Tooltip title="Phone Number" arrow>
                      📞{" "}
                      <a href={`tel:${report.teacher.phone_number}`} className="contact-link">
                      {report.teacher.phone_number}
                      </a>
                  </Tooltip>
                  </Typography>
              </Box>
            </Paper>


            {/* Quiz Reports */}
            {report.quizzes.map((quiz) => (
              <Paper elevation={2} key={quiz.quiz_id} className="quiz-container">
                <Box className="quiz-header">
                  <Typography variant="h6" className="quiz-title">
                    {quiz.title}
                  </Typography>
                  <BarChartIcon className="quiz-icon" />
                </Box>
                {quiz.sessions.length > 0 ? (
                  <Box className="sessions-container">
                    {quiz.sessions.map((session) => {
                      const expanded = expandedSessionId === session.session_id;
                      return (
                        <Paper elevation={2} className="session-card" key={session.session_id}>
                          <Box className="session-header">
                            <Box>
                              <Typography variant="subtitle1" className="session-user">
                                User: {session.user.username}
                              </Typography>
                              <Typography variant="body2" className="session-attempt">
                                Attempt: {session.attempt}
                              </Typography>
                              <Typography
                                variant="body2"
                                className="session-evaluation"
                                style={{
                                  color:
                                    session.final_evaluation > 75
                                      ? "green"
                                      : session.final_evaluation > 50
                                      ? "orange"
                                      : "red",
                                }}
                              >
                                Final Evaluation: {session.final_evaluation}%
                              </Typography>
                            </Box>
                            {/* ICON BUTTONS SECTION */}
                            <Box>
                              <IconButton onClick={() => toggleExpand(session.session_id)}>
                                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>

                              {/* NEW: REPORT BUTTON */}
                              <Tooltip title="AI Report" arrow>
                                <IconButton onClick={() => handleViewReportClick(session.session_id)}>
                                  <ArticleIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                          <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <Box className="answers-container">
                              {session.user_answers.map((answer, index) => (
                                <AnswerRow key={index} answer={answer} />
                              ))}
                            </Box>
                          </Collapse>
                        </Paper>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography className="no-sessions">No sessions available for this Assessment.</Typography>
                )}

              </Paper>
            ))}
          </Box>
        )}
      </Container>
      <ToastContainer />
      <ReportModal
        open={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        report={currentReport}
      />
    </div>
  );
}

export default TeacherSpecificPage;
